import classNames from 'classnames';
import { Button } from 'components/atoms/Button';
import Tooltip from 'components/atoms/Tooltip/Tooltip.component';
import { useHighlight } from 'hooks/useHighlight';
import type { FC, ReactNode } from 'react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import type { Duplicate } from 'types/global.types';
import { ExclamationCircleIcon, EyeIcon, TrashIcon } from 'utils/icons';

type DocumentDuplicateFinderProps = {
  onSelect: (_id: string, item: Duplicate) => void;
  onDelete: (dup: Duplicate) => Promise<boolean>;
  onFetch: () => Promise<Duplicate[]>;
  cacheKey?: string;
  renderLabel: (dup: Duplicate) => ReactNode;
  className?: string;
  viewText?: string;
  deleteText?: string;
  getShouldShowDeleteButton?: (dup: Duplicate) => boolean;
  highlightID?: string;
};

const DocumentDuplicateFinder: FC<DocumentDuplicateFinderProps> = (props) => {
  const { getShouldShowDeleteButton = () => true } = props;
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const autoCacheKey = useId();

  const [showMore, setShowMore] = useState(false);

  const cacheKey = props.cacheKey || autoCacheKey;
  const { isError, isLoading, data } = useQuery(cacheKey, props.onFetch, {
    cacheTime: 0,
  });

  const highlightAPI = useHighlight();

  const className = classNames(
    props.className,
    highlightAPI.shouldHighlight('duplicate-warning') && highlightAPI.className,
  );

  // TODO: maybe handle error and loading
  if (isError || isLoading || !data?.length) return null;

  const deleteDup = (deletedDup: Duplicate) => {
    queryClient.setQueryData<Duplicate[]>(cacheKey, (dups) =>
      (dups ?? []).filter((dup) => dup._id !== deletedDup._id),
    );
  };

  return (
    <div className={classNames('rounded-lg bg-yellow-50 p-4', className)}>
      <div className="flex">
        <ExclamationCircleIcon className="h-5 w-5 mr-2 text-yellow-400 relative top-[3px]" />
        <p className="font-semibold text-yellow-800">
          {t('duplicate_finder.found_title')}
        </p>
      </div>
      <ol className="flex flex-col mt-2 ml-7">
        {(showMore ? data : data.slice(0, 2)).map((dup, index, array) => {
          const isLast = array.length - 1 === index;
          return (
            <li
              key={index}
              className={classNames('py-2 flex flex-col', {
                'border-b border-primary-100': !isLast,
              })}
            >
              <div className="flex justify-between items-center gap-4">
                <span className="text-sm truncate text-yellow-700 leading-tight">
                  {props.renderLabel(dup)}
                </span>
                <div className="flex gap-4 relative">
                  <Tooltip
                    content={props.viewText}
                    placement="topRight"
                    hoverable={false}
                  >
                    <Button
                      asDefault
                      className="text-yellow-700 hover:text-yellow-600 focus:text-yellow-600"
                      // only expose the id on select, since the normal findOne data/logic can different than the duplicate search data/logic,
                      // and we dont wont to use the duplicate document as a full/valid expense/invoice document
                      onClick={() => props.onSelect(dup._id, dup)}
                    >
                      <EyeIcon className="h-5 w-5" />
                    </Button>
                  </Tooltip>
                  {getShouldShowDeleteButton(dup) && (
                    <Tooltip
                      content={props.deleteText}
                      placement="topRight"
                      hoverable={false}
                    >
                      <Button
                        asDefault
                        className="text-yellow-700 hover:text-yellow-600 focus:text-yellow-600"
                        onClick={() =>
                          props.onDelete(dup).then((isDeleted) => {
                            if (isDeleted) deleteDup(dup);
                          })
                        }
                      >
                        <TrashIcon className="h-5 w-5" />
                      </Button>
                    </Tooltip>
                  )}
                </div>
              </div>
              {dup.duplicateReason ? (
                <span className="font-semibold leading-tight text-sm text-yellow-700 truncate">
                  {t('duplicate_finder.reason.' + dup.duplicateReason)}
                </span>
              ) : null}
            </li>
          );
        })}
      </ol>
      {data.length > 2 && (
        <button
          type="button"
          onClick={() => setShowMore((bool) => !bool)}
          className={classNames(
            'text-yellow-700 hover:text-yellow-600 focus:text-yellow-600 ml-7 mt-3 font-semibold text-xs',
          )}
        >
          {showMore
            ? t('duplicate_finder.show_less')
            : t('duplicate_finder.show_more')}
        </button>
      )}
    </div>
  );
};

export default DocumentDuplicateFinder;
